import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { DEPARTMENTS, SPORTS } from "@/lib/departments";
import { ZigzagDivider, ZigzagDividerInverse, GeoAccents } from "@/components/GeoAccents";

const NEON = "#5BC2DE";

function DeptCard({ slug, hovered, setHovered }) {
  const d = DEPARTMENTS[slug];
  const isHovered = hovered === slug;
  const isOther = hovered && hovered !== slug;
  return (
    <Link
      to={`/d/${slug}`}
      data-testid={`department-${slug}-link`}
      onMouseEnter={() => setHovered(slug)}
      onMouseLeave={() => setHovered(null)}
      className="relative overflow-hidden flex-1 group cursor-pointer transition-[flex] duration-700 ease-out"
      style={{ flex: isHovered ? 1.4 : isOther ? 0.6 : 1 }}
    >
      {/* Triangles behind everything else */}
      <GeoAccents
        className="inset-0 w-full h-full opacity-0 group-hover:opacity-90 transition-opacity duration-500"
        color={d.accent}
      />

      <div
        className="absolute inset-0 bg-contain bg-bottom bg-no-repeat transition-transform duration-1000 ease-out group-hover:scale-105"
        style={{ backgroundImage: `url(${d.hero_image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 pointer-events-none" />
      <div
        className="absolute inset-0 transition-colors duration-700 pointer-events-none"
        style={{ backgroundColor: isHovered ? "transparent" : "rgba(0,0,0,0.4)" }}
      />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-12">
        <div className="flex items-center gap-3 mb-4">
          <img
            src={d.logo}
            alt={d.name}
            className="w-10 h-10 md:w-11 md:h-11 object-contain rounded"
          />
          <span className="csg-label" style={{ color: d.accent }}>
            {d.gender === "men" ? "Men's" : "Women's"} Division
          </span>
        </div>

        <h2 className="font-display text-[9vw] md:text-[7vw] leading-[0.85] uppercase">
          <span className="block text-white">{d.gender === "men" ? "Men's" : "Women's"}</span>
          <span className="block" style={{ color: d.accent }}>
            {d.sport}
          </span>
        </h2>
        <p className="font-body text-white/70 max-w-md mt-4 text-sm md:text-base">
          {d.tagline}
        </p>

        <div
          className={`mt-5 transition-all duration-500 ${
            isHovered ? "opacity-100 translate-x-0" : "opacity-60 -translate-x-2"
          }`}
        >
          <span className="csg-btn" style={{ backgroundColor: d.accent }}>
            Enter Division <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function DepartmentSelector() {
  const { sport } = useParams();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const meta = SPORTS[sport];
  if (!meta) {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="relative h-screen bg-black flex flex-col overflow-hidden">
      <header className="px-6 md:px-14 py-5 md:py-6 grid grid-cols-3 items-center shrink-0">
        <Link
          to="/"
          data-testid="back-to-sports"
          className="flex items-center gap-2 font-body text-xs md:text-sm tracking-[0.3em] uppercase text-white/60 hover:text-white"
        >
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="text-center">
          <div className="font-display text-xl md:text-2xl leading-none uppercase tracking-wide">
            <span className="text-white">COMPLETE</span>{" "}
            <span style={{ color: NEON }}>SPORTS</span>{" "}
            <span className="text-white">GROUP</span>
          </div>
        </div>
        <div className="text-right">
          <Link
            to="/admin/login"
            className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-white/60 hover:text-white"
          >
            Admin
          </Link>
        </div>
      </header>

      <section className="px-6 py-5 md:py-7 text-center shrink-0">
        <div className="csg-label mb-3" style={{ color: NEON }}>
          {meta.name}
        </div>
        <h1 className="font-display uppercase leading-[0.85] text-[10vw] md:text-[5.5vw]">
          <span className="block text-white">Choose Your</span>
          <span className="block" style={{ color: NEON }}>
            Division.
          </span>
        </h1>
      </section>

      <ZigzagDivider color={NEON} height={18} />

      <div className="flex-1 flex flex-col-reverse md:flex-row min-h-0" data-testid="dept-split">
        {meta.departments.map((slug, i) => (
          <React.Fragment key={slug}>
            <DeptCard slug={slug} hovered={hovered} setHovered={setHovered} />
            {i === 0 && <div className="hidden md:block w-px bg-white/10" />}
          </React.Fragment>
        ))}
      </div>

      <ZigzagDividerInverse color={NEON} height={18} />

      <footer className="px-6 md:px-14 py-3 flex items-center justify-between font-body text-xs tracking-[0.3em] uppercase text-white/40 shrink-0">
        <span>{meta.name} · Choose Division</span>
        <span className="hidden md:inline">Complete Sports Group</span>
      </footer>
    </div>
  );
}
